import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, List, Tag, Input, Empty, Space, message } from 'antd';
import { getCustomerAddresses } from '../../../services/logistics';
import type { CustomerAddress } from '../../../services/logistics';
import { COUNTRY_OPTIONS } from './AddressFields';

const countryLabel = (v?: string) => COUNTRY_OPTIONS.find((o) => o.value === v)?.label || v || '-';
const PAGE_SIZE = 10;

interface AddressBookImportModalProps {
  open: boolean;
  onClose: () => void;
  /** 'sender'：发件人导入弹窗，只显示"作为发件人"/"作为发件人或收件人"的记录；'recipient' 同理 */
  addressType: 'sender' | 'recipient';
  /** 传入时只显示这些国家/地区的地址（目前只会传单个国家，比如当前只支持美国境内运输时
   * 传 ['US']）——不是禁用不可选项，是直接不出现在列表里，包括"使用默认地址"这个按钮：
   * 客户设置的默认地址如果恰好不在这个范围内，按钮会因为查不到默认地址而自动禁用。 */
  restrictCountriesTo?: string[];
  onSelect: (address: CustomerAddress) => void;
}

/**
 * 从客户地址库导入发件人/收件人信息。地址类型的"并集"过滤（筛发件人时把"发件人或收件人"
 * 也带出来）由后端 CustomerAddressViewSet 的 address_type 参数本身就支持，这里直接传参即可。
 * 能看到哪些地址也是后端按登录账号的权限范围过滤好的，前端不用重复判断。
 *
 * "使用默认地址"这个按钮单独发一次请求拿默认地址，不从下面分页列表里找——
 * 分页之后默认地址完全可能不在当前这一页，用列表里现有数据判断会出现"明明设了默认地址，
 * 按钮却是禁用状态"这种假象。
 */
const AddressBookImportModal: React.FC<AddressBookImportModalProps> = ({
  open, onClose, addressType, restrictCountriesTo, onSelect,
}) => {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<CustomerAddress[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [defaultAddress, setDefaultAddress] = useState<CustomerAddress | null>(null);
  const [defaultLoading, setDefaultLoading] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const defaultField: 'is_default_origin' | 'is_default_recipient' =
    addressType === 'sender' ? 'is_default_origin' : 'is_default_recipient';
  // 后端 country 筛选是精确匹配，只在只允许唯一一个国家时才传（目前唯一实际场景：只允许
  // 美国）；以后如果允许多个国家同时可选，这里会退化成不传国家筛选、不做限制——真遇到
  // 那种场景再扩展成后端 country__in 或者前端二次过滤，现在没有这个需求，不提前设计。
  const countryFilter = restrictCountriesTo?.length === 1 ? restrictCountriesTo[0] : undefined;

  const fetchList = async (p: number, q?: string) => {
    setLoading(true);
    try {
      const resp = await getCustomerAddresses({
        address_type: addressType,
        page: p,
        page_size: PAGE_SIZE,
        search: q || undefined,
        is_active: true,
        country: countryFilter,
      });
      const body = resp.data as any;
      setList(Array.isArray(body?.results) ? body.results : (Array.isArray(body) ? body : []));
      setTotal(Number(body?.count ?? 0) || 0);
      setPage(p);
    } catch {
      message.error('获取地址库列表失败');
      setList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchDefault = async () => {
    setDefaultLoading(true);
    try {
      const resp = await getCustomerAddresses({
        address_type: addressType,
        page_size: 1,
        is_active: true,
        is_default_origin: defaultField === 'is_default_origin' ? true : undefined,
        is_default_recipient: defaultField === 'is_default_recipient' ? true : undefined,
        country: countryFilter,
      });
      const body = resp.data as any;
      const rows: CustomerAddress[] = Array.isArray(body?.results) ? body.results : (Array.isArray(body) ? body : []);
      setDefaultAddress(rows[0] || null);
    } catch {
      setDefaultAddress(null);
    } finally {
      setDefaultLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSearchText('');
      fetchList(1);
      fetchDefault();
    }
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, addressType, countryFilter]);

  // 搜索框：输入即搜索，防抖 400ms
  const handleSearchTextChange = (value: string) => {
    setSearchText(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => { fetchList(1, value); }, 400);
  };

  const handlePick = (addr: CustomerAddress) => {
    onSelect(addr);
    onClose();
  };

  const titleText = addressType === 'sender' ? '从地址库导入发件人' : '从地址库导入收件人';
  const defaultBtnText = addressType === 'sender' ? '使用默认发件人' : '使用默认收件人';
  const defaultTagText = addressType === 'sender' ? '默认发件人' : '默认收件人';

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      destroyOnClose
      title={(
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 24 }}>
          <span>{titleText}</span>
          <Button
            size="small"
            type="primary"
            disabled={!defaultAddress}
            loading={defaultLoading}
            onClick={() => defaultAddress && handlePick(defaultAddress)}
          >
            {defaultBtnText}
          </Button>
        </div>
      )}
    >
      <Input
        placeholder="搜索备注名/联系人/公司/城市/邮编"
        allowClear
        value={searchText}
        onChange={(e) => handleSearchTextChange(e.target.value)}
        style={{ marginBottom: 12 }}
      />
      <List
        loading={loading}
        dataSource={list}
        locale={{ emptyText: <Empty description="地址库里没有符合条件的地址" /> }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          size: 'small',
          showSizeChanger: false,
          onChange: (p) => fetchList(p, searchText),
        }}
        renderItem={(item) => {
          const isDefault = !!item[defaultField];
          return (
            <List.Item actions={[<Button key="import" size="small" onClick={() => handlePick(item)}>导入</Button>]}>
              <List.Item.Meta
                title={(
                  <Space>
                    <span>{item.label || item.name}</span>
                    {isDefault && <Tag color="gold">{defaultTagText}</Tag>}
                  </Space>
                )}
                description={`${item.name} · ${item.phone || '无电话'} · ${countryLabel(item.country)} ${item.state} ${item.city}`}
              />
            </List.Item>
          );
        }}
      />
    </Modal>
  );
};

export default AddressBookImportModal;
